// src/pages/GoingGammaLeadMagnet.js

import React, { useState, useEffect } from 'react';
import { useInView } from 'react-intersection-observer';
import { 
  SparklesIcon, 
  CheckCircleIcon, 
  PlayCircleIcon, 
  EyeIcon, 
  BoltIcon, 
  KeyIcon, 
  ChatBubbleOvalLeftEllipsisIcon,
  SpeakerWaveIcon
} from '@heroicons/react/24/solid';
import { useLocation } from 'react-router-dom';

// --- REAL IMPORTS FROM YOUR PROJECT ---
import { sendCodedLog } from '../../services/logService';
import Common from '../../common/Common';
import UrlConfig from '../../data/UrlConfig';
import { useAuth } from '../../context/AuthContext';

const API_URLS = UrlConfig;

const GoingGammaLeadMagnet = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState(localStorage.getItem('track_user_email') || '');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { user } = useAuth(); // <-- Existing Auth hook

  const location = useLocation();

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // ... (Your full handleSubmit logic remains unchanged)
    sendCodedLog({
      message: 'Click on "Yes, Send Me My Full Kit!" button',
      event: 'gamma_kit_submit_en',
      ...Common.getCommonLogData(location)
    });

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      setIsLoading(false);
      sendCodedLog({
        message: 'Frontend validation error: invalid email',
        event: 'gamma_kit_validation_error_en',
        data: { errorType: 'invalid_email' },
        ...Common.getCommonLogData(location)
      });
      return;
    }

    localStorage.setItem('track_user_email', email.trim());
    name && localStorage.setItem('track_user_name', name.trim());

    try {
      const response = await fetch(API_URLS.leads_add, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: email.trim(),
          name: name.trim(),
          folder: 'gamma-activation-kit',
          page: location.pathname,
          lang: 'en',
          newsletter: ['gamma-activation-kit'],
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setSubmitted(true);
        sendCodedLog({
          message: 'Gamma Kit form submitted successfully',
          event: 'gamma_kit_success_en',
          ...Common.getCommonLogData(location)
        });
      } else {
        throw new Error(data.message || 'An error occurred while processing your request.');
      }
    } catch (err) {
      setError(err.message);
      sendCodedLog({
        message: `Error submitting Gamma Kit form: ${err.message}`,
        event: 'gamma_kit_backend_error_en',
        data: { errorMessage: err.message },
        ...Common.getCommonLogData(location)
      });
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="relative bg-black text-white min-h-screen font-sans flex flex-col">
      <div className="absolute inset-0 bg-gradient-to-b from-gray-950 via-black to-gray-900 opacity-95 z-0"></div>
      
      <main ref={ref} className={`relative z-10 flex-grow container mx-auto px-4 py-12 flex flex-col items-center ${inView ? "fade-in-animation" : "opacity-0"}`}>
        
        <h1 className="text-4xl sm:text-5xl font-bold text-center mb-4 leading-tight">
          Unlock Your Higher Mind with the Gamma State
        </h1>
        <p className="text-lg sm:text-xl text-gray-400 text-center max-w-3xl mb-12">
          See the science behind this peak state of consciousness, learn a powerful activation technique, and get your free kit to go even deeper.
        </p>

        {/* --- JOE DISPENZA VIDEO (NOW FIRST) --- */}
        <div className="text-center w-full max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-8">The Science Behind Gamma Waves</h2>
            <div className="aspect-video w-full rounded-lg overflow-hidden shadow-2xl shadow-sky-500/20 border border-sky-700">
                <video 
                    data-track-click="joe-dispenza-gamma-video"
                    src="https://gusaguiar.com/assets/joe/gamma/Joe%20-%20Gamma%20-%20Intro.mp4"
                    className="w-full h-full bg-black"
                    controls
                    controlsList="nodownload"
                    poster="https://gusaguiar.com/assets/joe/gamma/Joe%20-%20Gamma.png"
                >
                    Your browser does not support the video tag.
                </video>
            </div>
            <p className="text-sm text-gray-500 mt-4 max-w-2xl mx-auto">
              Dr. Joe Dispenza demonstrates with real-time measurements how advanced meditators can enter the Gamma state to access higher levels of consciousness.
            </p>
        </div>

        {/* --- BASHAR'S TECHNIQUE VIDEO --- */}
        <div className="w-full max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl font-bold text-center mb-8">A Technique to Access the Gamma State</h2>
            <div className="aspect-video w-full rounded-lg overflow-hidden shadow-2xl shadow-sky-500/20 border border-sky-700">
                <iframe 
                    data-track-click="bashar-gamma-video"
                    src="https://www.youtube.com/embed/gOgRZc0Gv80" 
                    title="Bashar Gamma State Breathing Technique" 
                    frameBorder="0" 
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                    allowFullScreen
                    className="w-full h-full"
                ></iframe>
            </div>
        </div>

        <div className="text-center w-full max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-8">What is the Gamma State & Why Does It Matter?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-black/20 p-6 rounded-lg border border-gray-800 backdrop-blur-sm"><BoltIcon className="w-10 h-10 mx-auto mb-4 text-sky-400"/><h3 className="text-xl font-semibold mb-2">Peak Perception</h3><p className="text-gray-400">It's the brainwave frequency of high-level information processing, creativity, and intense focus.</p></div>
                <div className="bg-black/20 p-6 rounded-lg border border-gray-800 backdrop-blur-sm"><KeyIcon className="w-10 h-10 mx-auto mb-4 text-sky-400"/><h3 className="text-xl font-semibold mb-2">Unlock Your Potential</h3><p className="text-gray-400">Accessing this state allows you to bypass the critical "Physical Mind" to tap into your "Higher Mind."</p></div>
                <div className="bg-black/20 p-6 rounded-lg border border-gray-800 backdrop-blur-sm"><ChatBubbleOvalLeftEllipsisIcon className="w-10 h-10 mx-auto mb-4 text-sky-400"/><h3 className="text-xl font-semibold mb-2">Clearer Communication</h3><p className="text-gray-400">This is the state where intuition, synchronicity, and inspiration become your primary language.</p></div>
            </div>
        </div>

        {/* --- CONDITIONAL CONTENT BASED ON LOGIN STATE --- */}
        {user ? (
          // --- CONTENT FOR LOGGED-IN USERS ---
          <div className="text-center w-full max-w-4xl mx-auto mb-16">
            <h2 className="text-3xl font-bold mb-4 text-sky-400">Your Full Meditation Is Unlocked</h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-8">
              As a member, you have direct access to the complete 15-minute Gamma Holotropic Meditation. Relax, use headphones, and enjoy the journey.
            </p>
            <div className="aspect-video w-full rounded-lg overflow-hidden shadow-2xl shadow-purple-500/20 border border-purple-700">
                <video 
                    data-track-click="bashar-holotropic-meditation-video"
                    src="https://gusaguiar.com/assets/gamma-activation-kit/Bashar - Gamma Holotropic Meditation.mp4"
                    className="w-full h-full bg-black"
                    controls
                    // poster="https://gusaguiar.com/assets/fractal/Fratal - Gamma - 7m.jpg"
                >
                    Your browser does not support the video tag.
                </video>
            </div>
          </div>
        ) : (
          // --- CONTENT FOR LOGGED-OUT USERS (GUESTS) ---
          <>
            <div className="text-center w-full max-w-4xl mx-auto mb-16">
              <h2 className="text-3xl font-bold mb-4">Experience a Taste of the Gamma State Now</h2>
              <p className="text-lg text-gray-400 max-w-2xl mx-auto mb-6">
                Relax your mind and focus on the center of this 7-minute fractal meditation to begin activating your own Gamma state.
              </p>
              
              <div className="flex items-center justify-center gap-2 text-sky-400 mb-6">
                <SpeakerWaveIcon className="w-6 h-6"/>
                <span className="font-semibold">For a fully immersive experience, please use headphones.</span>
              </div>

              <div className="aspect-video w-full rounded-lg overflow-hidden shadow-2xl shadow-purple-500/20 border border-purple-700">
                  <video 
                      data-track-click="fractal-meditation-video"
                      src="https://gusaguiar.com/assets/fractal/Fratal - Gamma - 7m.mp4"
                      className="w-full h-full"
                      controls
                      loop
                      poster="https://gusaguiar.com/assets/fractal/Fratal - Gamma - 7m.jpg"
                  >
                      Your browser does not support the video tag.
                  </video>
              </div>
            </div>

            <div className="w-full max-w-4xl flex flex-col md:flex-row gap-8 items-center bg-gray-900/50 border border-purple-700/50 rounded-xl p-8 shadow-2xl shadow-purple-500/20 backdrop-blur-sm">
                <div className="w-full md:w-1/2">
                    <h3 className="font-bold text-2xl mb-4 text-sky-400">Ready to Go Deeper? Get Your Full Kit.</h3>
                    <p className="mb-6 text-gray-300">If you enjoyed that, sign up below to receive the complete "Gamma Activation Kit" in your inbox, totally free:</p>
                    <ul className="space-y-4">
                        <li className="flex items-start gap-3"><PlayCircleIcon className="w-6 h-6 text-purple-400 mt-1 flex-shrink-0" /><span><strong>The Full 15-Min Fractal Meditation:</strong> Get the complete, uninterrupted version of the meditation you just saw.</span></li>
                        <li className="flex items-start gap-3"><SparklesIcon className="w-6 h-6 text-sky-400 mt-1 flex-shrink-0" /><span><strong>15-Minute Holotrope Experience Gamma Meditation with Bashar:</strong> Dive into a deeper state of consciousness.</span></li>
                        <li className="flex items-start gap-3"><EyeIcon className="w-6 h-6 text-pink-400 mt-1 flex-shrink-0" /><span><strong>15-Min Kaleidoscopic Meditation:</strong> Dr. Joe Dispenza's immersive visual meditation.</span></li>
                    </ul>
                </div>
                
                <div className="w-full md:w-1/2 bg-black/30 border border-gray-700 rounded-xl p-6 sm:p-8">
                  {submitted ? (
                    <div className="text-center text-green-400 p-4 h-full flex flex-col justify-center items-center">
                      <CheckCircleIcon className="w-16 h-16 mx-auto mb-4" />
                      <p className="text-2xl font-semibold text-white">Success!</p>
                      <p className="text-lg">Your full kit is on its way to your inbox. Enjoy the journey!</p>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="flex flex-col space-y-5">
                      <h3 className="font-semibold text-lg text-center">Get Your Free Kit Now</h3>
                      <div>
                        <label htmlFor="name" className="block text-gray-300 text-sm font-medium mb-2">Your Name (Optional)</label>
                        <input type="text" id="name" value={name} onChange={(e) => setName(e.target.value)} className="w-full p-3 rounded-md bg-gray-900/70 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-sky-400" placeholder="So we can greet you properly"/>
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-gray-300 text-sm font-medium mb-2">Your Best Email</label>
                        <input type="email" id="email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full p-3 rounded-md bg-gray-900/70 border border-gray-600 focus:outline-none focus:ring-2 focus:ring-sky-400" placeholder="Where we'll send your kit" required/>
                      </div>
                      {error && <p className="text-sm text-center text-red-400">{error}</p>}
                      <button type="submit" 
                      data-track-click="submit-gamma-kit-form"
                      className="w-full text-lg py-3 px-4 rounded-xl bg-gradient-to-r from-sky-500 to-purple-600 font-semibold hover:from-sky-600 hover:to-purple-700 transition-all duration-300 disabled:opacity-50" disabled={isLoading}>
                        {isLoading ? 'Submitting...' : 'Yes, Send Me My Full Kit!'}
                      </button>
                    </form>
                  )}
                </div>
            </div>
          </>
        )}
      </main>

      <footer className="relative z-10 text-center py-6 text-xs text-gray-600">
        © {new Date().getFullYear()} Gus • Aguiar | On a mission to uplift and transform.
      </footer>
    </div>
  );
};

export default GoingGammaLeadMagnet;